import Bugsnag from "@bugsnag/js";
import { client } from "@nju/http-client";
import {
  BasicError,
  ContractError,
  err,
  HttpError,
  isHttpError,
  Result,
  UnknownError,
} from "@nju/result";
import { IOrchestratorsMap } from "./types";
import { serverResponse, verificationErrorResponse } from "./validators";

export async function createOrchestrator<T extends keyof IOrchestratorsMap>(
  type: T,
  params: IOrchestratorsMap[T]["params"],
): Promise<
  Result<ContractError | HttpError | UnknownError, IOrchestratorsMap[T]["type"]>
> {
  const result = await client<IOrchestratorsMap[T]["type"]>(
    `${process.env.NEXT_PUBLIC_PUBLIC_GATEWAY_HOST}/orchestrators`,
    {
      method: "post",
      validator: serverResponse,
      json: {
        type,
        ...params,
      },
      headers: {
        version: "2",
        apikey: process.env.NEXT_PUBLIC_APIKEY || "unknown",
      },
    },
  );

  if (result.isError) {
    Bugsnag.notify(result.err);
    const errorInfo = verificationErrorResponse.safeParse(
      result.err.extra?.json,
    );

    if (errorInfo.success && errorInfo.data.code === "50211") {
      return err(
        new BasicError({
          message: "No active products",
          code: "NO_ACTIVE_PRODUCTS",
        }),
      );
    }

    if (errorInfo.success && errorInfo.data.code === "50178") {
      return err(
        new BasicError({
          message: "user has unpaid invoices",
          code: "CLIENT_HAS_UNPAID_INVOICES",
        }),
      );
    }

    if (errorInfo.success && errorInfo.data.code === "50193") {
      return err(
        new BasicError({
          message: "orchestrator in use by other agent",
          code: "CLIENT_LOCKED_BY_OTHER_AGENT",
        }),
      );
    }

    if (errorInfo.success && errorInfo.data.code === "50245") {
      return err(
        new BasicError({
          message: "wrong data in payload orchestrator",
          code: "WRONG_DATA",
        }),
      );
    }

    if (errorInfo.success && errorInfo.data.code === "20034") {
      return err(
        new BasicError({
          message: "can't set previous code",
          code: "PREVIOUS_CODE",
        }),
      );
    }

    if (errorInfo.success && errorInfo.data.code === "50329") {
      return err(
        new BasicError({
          message: "another reactivation in progress",
          code: "ANOTHER_REACTIVATION_IN_PROGRESS",
        }),
      );
    }

    if (isHttpError(result.err) && result.err.status === 422) {
      return err(
        new BasicError({
          code: "422_UNPROCESSABLE_CONTENT",
          message: "Orchestrator creation not available yet",
        }),
      );
    }

    return result;
  }

  return result;
}
